import pRetry from 'p-retry';
import { ProboLogger } from './utils.js';
export const apiLogger = new ProboLogger('apiclient');
export class ApiError extends Error {
    constructor(status, message, data) {
        super(message);
        this.status = status;
        this.data = data;
        this.name = 'ApiError';
        // Remove stack trace for cleaner error messages
        this.stack = undefined;
    }
    toString() {
        return `${this.message} (Status: ${this.status})`;
    }
}
export class ApiClient {
    constructor(apiUrl, token, maxRetries = 3, initialBackoff = 1000) {
        this.apiUrl = apiUrl;
        this.token = token;
        this.maxRetries = maxRetries;
        this.initialBackoff = initialBackoff;
    }
    /**
     * Determines if an error should be retried.
     * Only retries on timeout and network errors, not on client/server errors.
     */
    isRetryableError(error) {
        var _a, _b, _c, _d, _e;
        // Network/connection errors should be retried
        if ((_a = error.message) === null || _a === void 0 ? void 0 : _a.includes('fetch failed'))
            return true;
        if ((_b = error.message) === null || _b === void 0 ? void 0 : _b.includes('network'))
            return true;
        if ((_c = error.message) === null || _c === void 0 ? void 0 : _c.includes('ETIMEDOUT'))
            return true;
        if ((_d = error.message) === null || _d === void 0 ? void 0 : _d.includes('ECONNRESET'))
            return true;
        if ((_e = error.message) === null || _e === void 0 ? void 0 : _e.includes('ECONNREFUSED'))
            return true;
        if (error.code === 'ETIMEDOUT')
            return true;
        if (error.code === 'ECONNRESET')
            return true;
        if (error.code === 'ECONNREFUSED')
            return true;
        // If it's an ApiError, check the status code
        if (error instanceof ApiError) {
            // Retry on timeout-related status codes
            if (error.status === 408)
                return true; // Request Timeout
            if (error.status === 502)
                return true; // Bad Gateway (temporary)
            if (error.status === 503)
                return true; // Service Unavailable (temporary)
            if (error.status === 504)
                return true; // Gateway Timeout
            if (error.status === 0)
                return true; // Network error
            // Don't retry on client errors (4xx) or server errors (5xx)
            // These indicate problems that won't be fixed by retrying
            return false;
        }
        // For unknown errors, don't retry to avoid masking issues
        return false;
    }
    isNetworkFailure(error) {
        var _a, _b;
        if (!error)
            return false;
        if (error instanceof ApiError && error.status === 0)
            return true;
        if ((_a = error.message) === null || _a === void 0 ? void 0 : _a.includes('fetch failed'))
            return true;
        const errorCode = error.code || ((_b = error === null || error === void 0 ? void 0 : error.cause) === null || _b === void 0 ? void 0 : _b.code);
        if (['ETIMEDOUT', 'ECONNRESET', 'ECONNREFUSED', 'ENOTFOUND', 'EAI_AGAIN', 'ECONNABORTED'].includes(errorCode)) {
            return true;
        }
        if (error.cause && this.isNetworkFailure(error.cause)) {
            return true;
        }
        return false;
    }
    formatNetworkErrorDetails(error, depth = 0) {
        if (!error || depth > 2) {
            return { message: String(error) };
        }
        const details = {
            name: error === null || error === void 0 ? void 0 : error.name,
            message: error === null || error === void 0 ? void 0 : error.message
        };
        for (const key of ['code', 'errno', 'syscall', 'hostname', 'address', 'port', 'type']) {
            if ((error === null || error === void 0 ? void 0 : error[key]) !== undefined) {
                details[key] = error[key];
            }
        }
        if (error === null || error === void 0 ? void 0 : error.cause) {
            details.cause = this.formatNetworkErrorDetails(error.cause, depth + 1);
        }
        return details;
    }
    logNetworkFailure(operationName, error, context) {
        if (!this.isNetworkFailure(error)) {
            return;
        }
        apiLogger.error(`[${operationName}] Network failure`, {
            apiUrl: this.apiUrl,
            attempt: context === null || context === void 0 ? void 0 : context.attempt,
            retriesLeft: context === null || context === void 0 ? void 0 : context.retriesLeft,
            details: this.formatNetworkErrorDetails(error)
        });
    }
    /**
     * Generic helper to wrap API requests with retry logic and consistent error handling.
     */
    async requestWithRetry(operationName, operation) {
        return pRetry(operation, {
            retries: this.maxRetries,
            minTimeout: this.initialBackoff,
            shouldRetry: (error) => {
                const shouldRetry = this.isRetryableError(error);
                if (!shouldRetry) {
                    if (this.isNetworkFailure(error)) {
                        this.logNetworkFailure(operationName, error);
                    }
                    else {
                        apiLogger.error(`${operationName} failed with non-retryable error: ${error.message || error}`);
                    }
                }
                return shouldRetry;
            },
            onFailedAttempt: error => {
                this.logNetworkFailure(operationName, error, {
                    attempt: error.attemptNumber,
                    retriesLeft: error.retriesLeft
                });
                apiLogger.warn(`${operationName} failed (retryable), attempt ${error.attemptNumber} of ${error.retriesLeft + error.attemptNumber}. Error: ${error.message}`);
            }
        });
    }
    async handleResponse(response) {
        var _a;
        try {
            const data = await response.json();
            const error = `${(data === null || data === void 0 ? void 0 : data.error) || 'Unknown error'}`;
            apiLogger.debug(`API response: ${JSON.stringify(data)}`);
            if (!response.ok) {
                switch (response.status) {
                    case 401:
                        throw new ApiError(401, `Unauthorized - Invalid or missing authentication token: ${error}`);
                    case 403:
                        throw new ApiError(403, `Forbidden - You do not have permission to perform this action: ${error}`);
                    case 400:
                        throw new ApiError(400, `Bad Request: ${error}`);
                    case 404:
                        throw new ApiError(404, `Not Found: ${error}`);
                    case 500:
                        throw new ApiError(500, `Internal Server Error: ${error}`);
                    default:
                        throw new ApiError(response.status, `API Error: ${error}`);
                }
            }
            return data;
        }
        catch (error) {
            // Only throw the original error if it's not a network error
            if (!((_a = error.message) === null || _a === void 0 ? void 0 : _a.includes('fetch failed'))) {
                throw error;
            }
            throw new ApiError(0, 'Network error: fetch failed');
        }
    }
    getHeaders() {
        const headers = {
            'Content-Type': 'application/json',
        };
        // Always include token in headers now that we have a default
        headers['Authorization'] = `Token ${this.token}`;
        return headers;
    }
    async createStep(options) {
        apiLogger.debug('creating step ', options.stepPrompt);
        return this.requestWithRetry('createStep', async () => {
            const response = await fetch(`${this.apiUrl}/step-runners/`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    step_id: options.stepIdFromServer,
                    scenario_name: options.scenarioName,
                    step_prompt: options.stepPrompt,
                    argument: options.argument,
                    initial_screenshot: options.initial_screenshot_url,
                    initial_html_content: options.initial_html_content,
                    use_cache: options.use_cache,
                    url: options.url,
                    action: options.action,
                    vanilla_prompt: options.vanilla_prompt,
                    is_vanilla_prompt_robust: options.is_vanilla_prompt_robust,
                    target_element_name: options.target_element_name,
                    position: options.position
                }),
            });
            const data = await this.handleResponse(response);
            return data.step.id;
        });
    }
    async patchStep(stepId, fields) {
        // Use PATCH /steps/:id/ endpoint with partial=true
        apiLogger.debug(`patching step #${stepId} with fields:`, Object.keys(fields));
        return this.requestWithRetry('patchStep', async () => {
            const response = await fetch(`${this.apiUrl}/steps/${stepId}/`, {
                method: 'PATCH',
                headers: this.getHeaders(),
                body: JSON.stringify(fields)
            });
            const data = await this.handleResponse(response);
            return;
        });
    }
    /* async resolveNextInstruction(stepId: string, instruction: Instruction | null, aiModel?: string) {
      apiLogger.debug(`resolving next instruction: ${instruction}`);
      return this.requestWithRetry('resolveNextInstruction', async () => {
        apiLogger.debug(`API client: Resolving next instruction for step ${stepId}`);
        
        const cleanInstruction = cleanupInstructionElements(instruction);
        
        const response = await fetch(`${this.apiUrl}/step-runners/${stepId}/run/`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify({
            executed_instruction: cleanInstruction,
            ai_model: aiModel
          }),
        });
        
        const data = await this.handleResponse(response);
        return data.instruction;
      });
    } */
    async uploadScreenshot(screenshot_bytes) {
        return this.requestWithRetry('uploadScreenshot', async () => {
            const response = await fetch(`${this.apiUrl}/upload-screenshots/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${this.token}`
                },
                body: screenshot_bytes,
            });
            const data = await this.handleResponse(response);
            return data.screenshot_url;
        });
    }
    async findStepById(stepId) {
        apiLogger.debug(`Finding step by id: ${stepId}`);
        return this.requestWithRetry('findStepById', async () => {
            const response = await fetch(`${this.apiUrl}/steps/${stepId}/`, {
                method: 'GET',
                headers: this.getHeaders(),
            });
            const data = await this.handleResponse(response);
            return data.step;
        });
    }
    async findStepByPrompt(prompt, scenarioName, url = '') {
        apiLogger.debug(`Finding step by prompt: ${prompt} and scenario: ${scenarioName}`);
        return this.requestWithRetry('findStepByPrompt', async () => {
            const response = await fetch(`${this.apiUrl}/step-runners/find-step-by-prompt/`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    prompt: prompt,
                    scenario_name: scenarioName,
                    url: url
                }),
            });
            try {
                const data = await this.handleResponse(response);
                return {
                    step: data.step,
                    total_count: data.total_count
                };
            }
            catch (error) {
                // If we get a 404, the step doesn't exist
                if (error instanceof ApiError && error.status === 404) {
                    return null;
                }
                // For any other error, rethrow
                throw error;
            }
        });
    }
    async resetStep(stepId) {
        return this.requestWithRetry('resetStep', async () => {
            const response = await fetch(`${this.apiUrl}/steps/${stepId}/reset/`, {
                method: 'POST',
                headers: this.getHeaders(),
            });
            const data = await this.handleResponse(response);
            return data;
        });
    }
    async interactionToStep(scenarioId, interaction, position = -1) {
        // Use POST /interaction-to-step/ endpoint
        // Backend will create new step or update existing one based on interaction_id
        apiLogger.debug(`converting interaction #${interaction.interactionId} to step`);
        return this.requestWithRetry('interactionToStep', async () => {
            var _a, _b, _c, _d;
            const response = await fetch(`${this.apiUrl}/interaction-to-step/`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    scenario_id: scenarioId,
                    position,
                    interaction_id: interaction.interactionId,
                    action: interaction.action,
                    argument: interaction.argument,
                    element_css_selector: ((_a = interaction.elementInfo) === null || _a === void 0 ? void 0 : _a.css_selector) || '',
                    iframe_selector: ((_b = interaction.elementInfo) === null || _b === void 0 ? void 0 : _b.iframe_selector) || '',
                    smart_selector: ((_c = interaction.elementInfo) === null || _c === void 0 ? void 0 : _c.smart_selector) || null,
                    smart_iframe_selector: ((_d = interaction.elementInfo) === null || _d === void 0 ? void 0 : _d.smart_iframe_selector) || null,
                    prompt: interaction.nativeDescription,
                    vanilla_prompt: interaction.nativeDescription,
                    is_vanilla_prompt_robust: interaction.isNativeDescriptionElaborate || false,
                    target_element_name: interaction.nativeName,
                    target_element_info: interaction.elementInfo,
                    url: interaction.url,
                    is_secret: interaction.isSecret || false,
                })
            });
            const data = await this.handleResponse(response);
            return [data.result.step_id, data.result.matched_step, data.scenario_id];
        });
    }
    async actionToPrompt(action2promptInput, aiModel) {
        // Use POST /action-to-prompt/ endpoint
        apiLogger.debug(`running action2prompt for step #${action2promptInput.step_id}`);
        return this.requestWithRetry('actionToPrompt', async () => {
            const response = await fetch(`${this.apiUrl}/steps/${action2promptInput.step_id}/action_to_prompt/`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({ ...action2promptInput, 'model': aiModel })
            });
            const data = await this.handleResponse(response);
            return data;
        });
    }
    async summarizeScenario(scenarioId, aiModel) {
        return this.requestWithRetry('summarizeScenario', async () => {
            const response = await fetch(`${this.apiUrl}/api/scenarios/${scenarioId}/summary`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({ 'model': aiModel })
            });
            const data = await this.handleResponse(response);
            return data;
        });
    }
    async askAI(question, scenarioName, screenshot, aiModel) {
        apiLogger.debug(`Asking AI question: "${question}", scenarioName: ${scenarioName}`);
        apiLogger.debug(`headers: ${JSON.stringify(this.getHeaders())}`);
        return this.requestWithRetry('askAI', async () => {
            const response = await fetch(`${this.apiUrl}/api/ask-ai/`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    question: question,
                    scenario_name: scenarioName,
                    screenshot: screenshot,
                    model: aiModel
                }),
            });
            const data = await this.handleResponse(response);
            return data;
        });
    }
    async screenshotReasoning(stepId, prompt, aiModel) {
        apiLogger.debug(`Performing screenshot reasoning for step: ${stepId}`);
        return this.requestWithRetry('screenshotReasoning', async () => {
            const response = await fetch(`${this.apiUrl}/steps/${stepId}/screenshot_reasoning/`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    prompt: prompt,
                    model: aiModel
                }),
            });
            const data = await this.handleResponse(response);
            return data.action;
        });
    }
    async findBestCandidateElement(stepId, candidatesScreenshotUrl, candidateElements, aiModel) {
        apiLogger.debug(`Finding best candidate element for step: ${stepId}`);
        return this.requestWithRetry('findBestCandidateElement', async () => {
            const response = await fetch(`${this.apiUrl}/steps/${stepId}/find_best_candidate_element/`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    screenshot_url: candidatesScreenshotUrl,
                    candidate_elements: candidateElements,
                    model: aiModel
                }),
            });
            const data = await this.handleResponse(response);
            return data.index;
        });
    }
    async getProjects() {
        return this.requestWithRetry('getProjects', async () => {
            const response = await fetch(`${this.apiUrl}/projects/`, {
                method: 'GET',
                headers: this.getHeaders()
            });
            return this.handleResponse(response);
        });
    }
} /* ApiClient */
//# sourceMappingURL=api-client.js.map